import {
  IresDevice,
  IresDeviceStatus,
  isPoweredOff,
  IRES_TIMEOUT_THRESHOLD_DEFAULT_IN_MS,
} from "./IresDevice";
import { firestore } from "firebase/app";
import * as base64Buffer from "base64-arraybuffer";

import {
  ComBoxSensorType,
  getSensorDataLengthFromSensorType,
  getSensorTypeFromFlag,
  isBleResistanceSensorType,
  ROOM_SENSOR_INDEX,
  TOTAL_SENSOR_COUNT,
  readSensorData,
} from "@/data/models/ComBoxSensorData";
import { ProgramChain } from "./ProgramChain";
import { RunOverview } from "./RunOverview";
import {
  ComBoxDryingConfigInfo,
  getComBoxDryingProgramsDryingConfigInfo,
  getDefaultDryingPrograms,
  ComBoxDryingProgram,
} from "./ComBoxDryingProgram";

import { isBitSet } from "@/utils/ByteUtils";
import { IresDeviceResponseReader } from "@/utils/IresDeviceResponseReader";
import { hasCapability, DeviceCapability } from "./DeviceCapability";

export enum ComBoxType {
  HYDRY_BOX = "HYDRY_BOX",
  HYDRY_MINI = "HYDRY_MINI",
}

export function isHyDryMiniSerial(serialNumber: string): boolean {
  return serialNumber.toUpperCase().startsWith("BM");
}

export function getComBoxType(comBox: ComBox): ComBoxType {
  if (comBox.comBoxType) {
    return comBox.comBoxType;
  }
  return isHyDryMiniSerial(comBox.serialNo) ? ComBoxType.HYDRY_MINI : ComBoxType.HYDRY_BOX;
}

export function isHyDryMini(comBox: ComBox): boolean {
  return getComBoxType(comBox) === ComBoxType.HYDRY_MINI;
}

export interface HardwareConfiguration {
  sampleMemory?: "FLASH_4MB" | "FLASH_8MB";
  microcontrollerType?: "STM32F4" | "STM32U5";
  modemType?: "QUECTEL_BC66" | "QUECTEL_BC68" | "QUECTEL_BG77";
}

export enum ComState {
  LID_OPEN = "LID_OPEN",
}

export enum DryingPauseType {
  INACTIVE = "INACTIVE",
  SUSPEND = "SUSPEND",
  NIGHT_MODE = "NIGHT_MODE",
  PROGRAM = "PROGRAM",
  MANUAL = "MANUAL",
  INFINITE = "INFINITE",
  ROOM_DRYING_TEMPERATURE_TOO_HIGH = "ROOM_DRYING_TEMPERATURE_TOO_HIGH",
  ROOM_DRYING_HUMIDITY_THRESHOLD_REACHED = "ROOM_DRYING_HUMIDITY_THRESHOLD_REACHED",
  POWER_OVERLOAD = "POWER_OVERLOAD",
}

export enum ComBoxDeviceState {
  POWER_FAIL = "POWER_FAIL",
  TRANSITION = "TRANSITION",
  STANDBY = "STANDBY",
  IDLE = "IDLE",
  ACTIVE = "ACTIVE",
  SUSPENDED = "PAUSED",
  WAITING = "WAITING",
  DRY_RUN_ENDED = "DRY_RUN_ENDED",
  MALFUNCTION = "MALFUNCTION",
}

export function getDryingPauseTypeFromFlag(dryingPauseTypeFlag: number): DryingPauseType {
  const pauseTypeBits = dryingPauseTypeFlag % 16;

  switch (pauseTypeBits) {
    case 0: return DryingPauseType.INACTIVE;
    case 1: return DryingPauseType.SUSPEND;
    case 2: return DryingPauseType.NIGHT_MODE;
    case 3: return DryingPauseType.PROGRAM;
    case 4: return DryingPauseType.MANUAL;
    case 5: return DryingPauseType.INFINITE;
    case 6: {
      const isTemperatureTooHigh = isBitSet({ value: dryingPauseTypeFlag, bitIndex: 4 });

      if (isTemperatureTooHigh) {
        return DryingPauseType.ROOM_DRYING_TEMPERATURE_TOO_HIGH;
      } else {
        return DryingPauseType.ROOM_DRYING_HUMIDITY_THRESHOLD_REACHED;
      }
    }
    case 7: return DryingPauseType.POWER_OVERLOAD;
    default: return DryingPauseType.INACTIVE;
  }
}

export function formatActiveDryingPauseType(
  vue: Vue,
  dryingPauseType: DryingPauseType
) {
  switch (dryingPauseType) {
    case DryingPauseType.MANUAL:
      return vue.$t("comBox.dryingPauseType.manual");
    case DryingPauseType.PROGRAM:
      return vue.$t("comBox.dryingPauseType.program");
    case DryingPauseType.ROOM_DRYING_TEMPERATURE_TOO_HIGH:
      return vue.$t("comBox.dryingPauseType.temperatureTooHigh");
    case DryingPauseType.ROOM_DRYING_HUMIDITY_THRESHOLD_REACHED:
      return vue.$t("comBox.dryingPauseType.humidityThresholdReached");
    case DryingPauseType.POWER_OVERLOAD:
      return vue.$t("comBox.dryingPauseType.powerOverload");
    default:
      return null;
  }
}

export function formatDryingPauseType(
  vue: Vue,
  dryingPauseType: DryingPauseType
) {
  switch (dryingPauseType) {
    case DryingPauseType.MANUAL:
      return vue.$t("sampleList.dryingPauseTypes.manual").toString();
    case DryingPauseType.PROGRAM:
      return vue.$t("sampleList.dryingPauseTypes.program").toString();
    case DryingPauseType.ROOM_DRYING_TEMPERATURE_TOO_HIGH:
      return vue.$t("sampleList.dryingPauseTypes.temperatureTooHigh").toString();
    case DryingPauseType.ROOM_DRYING_HUMIDITY_THRESHOLD_REACHED:
      return vue.$t("sampleList.dryingPauseTypes.humidityThresholdReached").toString();
    case DryingPauseType.POWER_OVERLOAD:
      return vue.$t("sampleList.dryingPauseTypes.powerOverload").toString();
    case DryingPauseType.NIGHT_MODE:
      return vue.$t("sampleList.dryingPauseTypes.nightMode").toString();
    case DryingPauseType.SUSPEND:
      return vue.$t("sampleList.dryingPauseTypes.suspend").toString();
    case DryingPauseType.INFINITE:
      return vue.$t("sampleList.dryingPauseTypes.infinite").toString();
    default:
      return vue.$t("sampleList.dryingPauseTypes.inactive").toString();
  }
}

export function formatActiveDryingPauseState(
  vue: Vue,
  newDryingPauseType: DryingPauseType,
  lastDryingPauseType: DryingPauseType
) {
  const formattedNewDryingPauseType = formatDryingPauseType(vue, newDryingPauseType);
  const formattedLastDryingPauseType = formatDryingPauseType(vue, lastDryingPauseType);
  return vue.$t("sampleList.formatDryingPauseState", [formattedLastDryingPauseType, formattedNewDryingPauseType]).toString(); 
}

export enum RelayState {
  ON,
  NIGHT_MODE,
  OFF,
}

export function getRelayState(flag: number) {
  const mod = flag % 4;

  return mod == 3
    ? RelayState.ON
    : mod == 1
    ? RelayState.NIGHT_MODE
    : RelayState.OFF;
}

export function getRelay1State(comBoxStatus: ComBoxStatus) {
  return getRelayState(comBoxStatus.relayState);
}

export function getRelay2State(comBoxStatus: ComBoxStatus) {
  return getRelayState(comBoxStatus.relayState >>> 2);
}

export function isNightModeActive(comBoxStatus: ComBoxStatus) {
  return (
    getRelay1State(comBoxStatus) === RelayState.NIGHT_MODE ||
    getRelay2State(comBoxStatus) === RelayState.NIGHT_MODE
  );
}

export interface ControlConfigInfo {
  comBoxFirmwareVersion: string;
  dataBase64: string;
}

export interface ComBox extends IresDevice {
  comBoxStatus: ComBoxStatus;
  startUpEnergyInKwh: number;

  comBoxFirmwareVersion: string;
  displayFirmwareVersion: string;

  comBoxType?: ComBoxType;
  hardwareConfiguration?: HardwareConfiguration;

  currentControlConfig?: ControlConfigInfo;
  defaultControlConfig?: ControlConfigInfo;
  dryingConfigInfo?: ComBoxDryingConfigInfo;

  mcuResetSource?: number;
}

export interface ComBoxStatus extends IresDeviceStatus {
  sensorTypes: ComBoxSensorType[];
  dryRunActive: boolean;
  relayState: number;
  sensorData: string;
  comState: ComState[];
  deviceState: number;
  latestRunId: number;
  dryRunFirstRunId: number;
  online: boolean;
  dryRunStartDate: firestore.Timestamp;
  dryingPauseType: DryingPauseType;

  dryRunInitialMeterReadingInKwh: number;
  currentMeterReadingInKwh: number;

  dryRunInitialHumidity: null | number;
  dryRunInitialTemperature: null | number;

  activeProgram: number;
  step: number;
  cycle: number;

  batteryVoltage: number;

  errorState: string;

  currentGuideSensor: number;

  offsetToUtc: number;

  sensorDataExt?: string;
  sensorFlagsExt?: number[];

  /**
   * Per-slot BLE sensor serial numbers, keyed by slot number (as string).
   * Populated by Cloud Function from IoT payloads. Used to determine
    * sensor form factor suffix (WLB → L, WSB → S, WMB → M).
   */
  sensorSlotSerials?: Record<string, string>;
}

/**
 * ==================== FIRMWARE v4.0.0+ CHANGES ====================
 * Maps firmware slot numbers (1–9) to frontend-compatible array indexes (0–8).
 *
 * Context:
 * - On the frontend, external sensors are displayed as Slot 1–8, while the room sensor is treated as a special sensor (not assigned a slot).
 * - In Firestore, arrays are structured such that the room sensor data always resides at index 4 for consistency.
 * - When reading data from a device (new firmware), data is rearranged so that the room sensor’s info is stored at index 4 before saving to Firestore.
 * - When displaying data (e.g., in logs or UI), the mapping ensures external sensors are shown as Slot 1–8, and the room sensor remains distinct.
 * - This mapping logic supports both new and legacy firmware correctly.
 * 
 * - Firmware represents up to 8 external sensors in slots 1–8, and a room sensor in slot 9.
 * - Legacy firmware supported only 4 external sensors (slots 1–4), with the room sensor at slot 5.
 * - On the frontend, the room sensor is always treated as slot 5 (index 4).
 * - These helpers exist to maintain backward compatibility when rearranging sensor data or flags.
 * - Rearranging is only necessary if the data is saved in a decoded (human-readable) format.
 * - If the data is still base64 encoded, it is not rearranged at that point.
 *
 * @param slotNo The slot number from firmware (1–9).
 * @returns The corresponding frontend array index (0–8), with the room sensor always mapped to index 4.
 */
export function calculateIndexFromSlotNumber(slotNo: number) {
  if (slotNo < 5) {
    return slotNo - 1;
  }
  else if (slotNo === 9) {
    return 4;
  }
  else {
    return slotNo;
  }
}

/**
 * ==================== FIRMWARE v4.0.0+ CHANGES ====================
 * Maps updated frontend slot numbers (indexes 0–8, with room sensor at 5) back to firmware slot numbers (1–9).
 *
 * Context:
 * - On the frontend, external sensors are displayed as Slot 1–8, while the room sensor is treated as a special sensor (not assigned a slot).
 * - In Firestore, arrays are structured such that the room sensor data always resides at index 4 for consistency.
 * - When reading data from a device (new firmware), data is rearranged so that the room sensor’s info is stored at index 4 before saving to Firestore.
 * - When displaying data (e.g., in logs or UI), the mapping ensures external sensors are shown as Slot 1–8, and the room sensor remains distinct.
 * - This mapping logic supports both new and legacy firmware correctly.
 * 
 * - Firmware uses slots 1–8 for external sensors and slot 9 for the room sensor.
 * - Legacy firmware had only 4 external sensors and the room sensor at slot 5.
 * - On the frontend, the room sensor is always handled as slot 5 (index 4).
 * - This function performs the inverse of `calculateIndexFromSlotNumber` for backward compatibility.
 * - Rearranging is only necessary if the data is saved in a decoded (human-readable) format.
 * - If the data is still base64 encoded, it is not rearranged at that point.
 *
 * @param slotNo The updated slot number or frontend array index (1–8, with room sensor at 5).
 * @returns The corresponding firmware slot number (1–9), with the room sensor mapped to 9.
 */
export function calculateSlotNumberFromIndex(index: number) {
  if (index < 4) {
    return index + 1;
  }
  else if (index === 4) {
    return 9;
  }
  else {
    return index;
  }
}

/**
 * Looks up a BLE sensor serial number from the sensorSlotSerials map.
 * @param sensorSlotSerials Per-slot serials from comBoxStatus or run doc (keyed by slot number string)
 * @param sensorIndex Frontend sensor index (0–8)
 */
export function getSerialForSensorIndex(
  sensorSlotSerials: Record<string, string> | undefined,
  sensorIndex: number,
): string | undefined {
  if (sensorSlotSerials) {
    const slotNumber = calculateSlotNumberFromIndex(sensorIndex);
    return sensorSlotSerials[String(slotNumber)];
  }
  return undefined;
}

/**
 * Aggregates sensor slot serials from program chain runs, with an optional
 * fallback to comBoxStatus serials for **active** dryings only.
 *
 * Resolution order:
 * 1. Serials stored in run overviews (first non-empty serial per slot wins).
 * 2. For active dryings: comBoxStatus.sensorSlotSerials (sensors currently connected).
 * 3. Per-sensor last-resort: handled by `getSensorSuffix` — MINI → S, BOX → L.
 *
 * Historical dryings must NOT use comBoxStatus because sensors may have been
 * swapped since the drying ended. For those, step 3 is the only fallback.
 *
 * @param runs           Run overviews from the program chain (may lack sensorSlotSerials in older data)
 * @param activeFallback comBoxStatus.sensorSlotSerials — pass ONLY when the chain is active
 * @returns Merged sensor slot serials map (may be empty if no source has data)
 *
 * @see getSensorSuffix — per-sensor fallback when no serial is available
 * @see formatSensorSlotLabel — uses the result to build slot labels like "1L", "2S"
 * @see getSerialForSensorIndex — looks up a single slot from the result
 *
 * Used by:
 * - SelectionTable.vue (graph legend column headers)
 * - DryingProgressGraphDataExtractor.ts (graph dataset labels)
 * - DryingInfoCalculator.ts (drying info sensor descriptions)
 */
export function aggregateSensorSlotSerials(
  runs: Pick<RunOverview, 'sensorSlotSerials'>[],
  activeFallback?: Record<string, string>,
): Record<string, string> {
  const aggregated: Record<string, string> = {};

  for (const run of runs) {
    if (run.sensorSlotSerials) {
      for (const [slot, serial] of Object.entries(run.sensorSlotSerials)) {
        if (!aggregated[slot]) {
          aggregated[slot] = serial;
        }
      }
    }
  }

  // For active dryings, fall back to comBoxStatus when runs lack serials
  if (Object.keys(aggregated).length === 0 && activeFallback) {
    return { ...activeFallback };
  }

  return aggregated;
}

/** Sensor form factor suffix: L (long/wired), S (short BLE), M (material), R (BLE type 5). */
export type SensorSuffix = "L" | "S" | "M" | "R";

/**
 * Determines the sensor form factor suffix from sensor type and optional BLE serial number.
 * - Wired sensors (TELAIRE, SHT3X, RADON) → L
 * - BLE type 5 sensors → R (regardless of serial; serial prefix WRB)
 * - BLE type 3 / material sensors → M (regardless of serial; serial prefix WMB)
 * - BLE sensors with WSB serial → S (short BLE sensor)
 * - BLE sensors with WLB serial or unknown serial → L (long BLE sensor)
 * - MINI devices with unknown BLE serial → S (MINI sensors default to short)
 * - EMPTY/UNKNOWN → L (fallback)
 */
export function getSensorSuffix(
  sensorType?: ComBoxSensorType,
  serialNumber?: string,
  isMini?: boolean,
): SensorSuffix {
  if (
    sensorType === ComBoxSensorType.BLE_SENSOR_TYPE_1 ||
    sensorType === ComBoxSensorType.BLE_SENSOR_TYPE_3 ||
    sensorType === ComBoxSensorType.BLE_SENSOR_TYPE_MATERIAL ||
    sensorType === ComBoxSensorType.BLE_SENSOR_TYPE_5
  ) {
    const normalizedSerial = serialNumber?.toUpperCase();

    // BLE type 5 and material sensors are identified by their type, so they keep
    // their suffix even when no serial has been reported yet (sensorSlotSerials is
    // sparse, while the sensor type arrives with every status message).
    if (
      sensorType === ComBoxSensorType.BLE_SENSOR_TYPE_5 ||
      normalizedSerial?.startsWith("WRB")
    ) {
      return "R";
    }

    if (isBleResistanceSensorType(sensorType) || normalizedSerial?.startsWith("WMB")) {
      return "M";
    }

    if (normalizedSerial?.startsWith("WSB")) {
      return "S";
    }
    if (normalizedSerial?.startsWith("WLB")) {
      return "L";
    }
    // Unknown serial: MINI defaults to short, BOX defaults to long
    return isMini ? "S" : "L";
  }
  return "L";
}

/**
 * Formats a sensor slot label with its form factor suffix.
 * @returns e.g. "1L", "2S", "3M" for floor sensors, or undefined for room sensor.
 */
export function formatSensorSlotLabel(
  sensorIndex: number,
  sensorType?: ComBoxSensorType,
  serialNumber?: string,
  isMini?: boolean,
): string | undefined {
  if (sensorIndex === ROOM_SENSOR_INDEX) return undefined;
  const slotNumber = calculateSlotNumberFromIndex(sensorIndex);
  const suffix = getSensorSuffix(sensorType, serialNumber, isMini);
  return `${slotNumber}${suffix}`;
}

export function doesComBoxSupportRoomDrying(comBox: ComBox) {
  return hasCapability(comBox, DeviceCapability.ROOM_DRYING);
}

export function doesComBoxTargetDrying(comBox: ComBox) {
  return hasCapability(comBox, DeviceCapability.EXTENDED_SENSORS);
}

export function doesComBoxSupportAdditionalSensors(comBox: ComBox) {
  return hasCapability(comBox, DeviceCapability.EXTENDED_SENSORS);
}

export function getComBoxOffsetToUtc(comBox: ComBox) {
  // Sometimes, Josef needs dummy devices for testing, and for these devices, we don’t have the comBoxStatus field
  if (!comBox.comBoxStatus) {
    return 2;
  }

  const offset = comBox.comBoxStatus.offsetToUtc;

  //TODO Remove this once all boxes have loaded the offest
  return offset === undefined ? 2 : offset;
}

export function getNumRunsInDryRun(comBoxStatus: ComBoxStatus) {
  if (!comBoxStatus.dryRunActive) {
    return undefined;
  }

  const latestRunId = comBoxStatus.latestRunId;
  const dryRunFirstRunId = comBoxStatus.dryRunFirstRunId;

  if (latestRunId < dryRunFirstRunId) {
    return 2 + latestRunId + (255 - dryRunFirstRunId);
  } else {
    return 1 + latestRunId - dryRunFirstRunId;
  }
}

/**
 * Sensor status flag bits, as the ComBox reports them per slot in `sensorDataExt` and as the Cloud
 * mirrors them into `comBoxStatus.sensorFlagsExt` on every status message.
 *
 * This is the LIVE state of a sensor. The event 77 log entries (`SensorLegendMetadata`) are the
 * edges of these same bits, kept so a finished drying can still be reconstructed; where both are
 * available the flags win, because a replay can only be as complete as the events it was given.
 *
 * Mirrors `SENSOR_FLAG_RULES` in IRES Cloud
 * (`functions/src/services/hydrydevice/LogHyDryDeviceEvent.ts`). The two tables have to stay in
 * step: a divergence produces a wrong badge with no error anywhere.
 */
export enum ComBoxSensorFlag {
  BATTERY_WEAK = 0x01,
  LINK_WEAK = 0x04,
  VALUE_OUT_OF_RANGE = 0x10,
  LINK_TIMEOUT = 0x20,
  BUTTON_PRESSED = 0x40,
  TURNED_OFF = 0x80,
}

/**
 * Flag bits that count as a warning, mapped to the same key vocabulary the event 77 replay
 * produces (`WARNING_KEY_MAP` in `SensorLegendMetadata.ts`), so both sources feed one renderer.
 *
 * `VALUE_OUT_OF_RANGE` and `BUTTON_PRESSED` are deliberately absent. A pressed button is not a
 * fault, and out-of-range has no status string yet; both are unreported by the event path too, so
 * leaving them out keeps the flag and replay sources showing the same set of states.
 */
const SENSOR_FLAG_WARNING_KEYS: Record<string, ComBoxSensorFlag> = {
  sensorBatteryWeak: ComBoxSensorFlag.BATTERY_WEAK,
  sensorLinkWeak: ComBoxSensorFlag.LINK_WEAK,
  sensorLinkTimeout: ComBoxSensorFlag.LINK_TIMEOUT,
  sensorTurnedOff: ComBoxSensorFlag.TURNED_OFF,
};

/**
 * Decodes one slot's flag byte into warning keys.
 *
 * @param flags One entry of `comBoxStatus.sensorFlagsExt`; `undefined` for a box on firmware older
 *              than v4.0.0, which sends no flags at all and must fall back to the event replay.
 * @returns The active warning keys, empty when the sensor is healthy.
 */
export function getSensorWarningsFromFlags(flags?: number): string[] {
  if (!flags) {
    return [];
  }

  return Object.keys(SENSOR_FLAG_WARNING_KEYS).filter(
    (key) => (flags & SENSOR_FLAG_WARNING_KEYS[key]) !== 0
  );
}

function extractSensorTypesFromSensorDataExt(sensorDataExt: string) {
  const payload = new Uint8Array(base64Buffer.decode(sensorDataExt));
  const iresDeviceResponseReader = new IresDeviceResponseReader(payload);

  const sensorTypes: ComBoxSensorType[] = Array(TOTAL_SENSOR_COUNT).fill(ComBoxSensorType.EMPTY);

  // Skip first 2 bytes; Bytes Following, Type
  iresDeviceResponseReader.skip(2);
  let dataOffset = 2;

  // We subtract 1 from the total length because last byte is the aggregated flag which we currently ignore
  while (dataOffset + 1 <= payload.length - 1) {
    const typeFromSensorDeviceType = iresDeviceResponseReader.readUint8();
    dataOffset++;

    if (typeFromSensorDeviceType === 0) {
      // Empty slot detected
      continue;
    }
  
    if (dataOffset > payload.length) {
      // Prevent reading out of bounds
      break;
    }

    const sensorType = getSensorTypeFromFlag(typeFromSensorDeviceType);
    const slotNo = iresDeviceResponseReader.readUint8();
    dataOffset++;

    const sensorIndex = calculateIndexFromSlotNumber(slotNo);

    if (!Number.isInteger(sensorIndex) || sensorIndex < 0 || sensorIndex >= TOTAL_SENSOR_COUNT) {
      // A slot number outside 1-9 means the record walk lost its alignment
      // (corrupt payload, or a sensor entry with an unexpected size); a
      // non-integer index means the entry ran past the end of the payload.
      break;
    }

    sensorTypes[sensorIndex] = sensorType;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const flags = iresDeviceResponseReader.readUint8();
    dataOffset++;
    
    const dataLength = getSensorDataLengthFromSensorType(sensorType)
    iresDeviceResponseReader.skip(dataLength);
    dataOffset += dataLength;
  }

  return sensorTypes;
}

function extractSensorDataFromSensorDataExt(sensorDataExt: string) {
  const payload = new Uint8Array(base64Buffer.decode(sensorDataExt));
  const iresDeviceResponseReader = new IresDeviceResponseReader(payload);

  // We expect up to 9 sensors (0–8)
  const sensorData = new Uint8Array(18); // Assuming 2 bytes per sensor slot
  let sensorDataIndex = 0;

  // Skip first 2 bytes; Bytes Following, Type
  iresDeviceResponseReader.skip(2);
  let dataOffset = 2;

  // We subtract 1 from total length because the last byte is the aggregated flag (ignored)
  while (dataOffset + 1 <= payload.length - 1) {
    const typeFromSensorDeviceType = iresDeviceResponseReader.readUint8();
    dataOffset++;

    if (typeFromSensorDeviceType === 0) {
      // Empty slot detected, skip next fields (slot, flags) but fill with zero bytes for consistency
      iresDeviceResponseReader.skip(3);
      dataOffset += 3;
      sensorData[sensorDataIndex++] = 0x00;
      sensorData[sensorDataIndex++] = 0x00;
      continue;
    }

    if (dataOffset > payload.length) {
      // Prevent reading out of bounds
      break;
    }

    const sensorType = getSensorTypeFromFlag(typeFromSensorDeviceType);
    const slotNo = iresDeviceResponseReader.readUint8();
    dataOffset++;

    const sensorIndex = calculateIndexFromSlotNumber(slotNo);

    if (!Number.isInteger(sensorIndex) || sensorIndex < 0 || sensorIndex >= TOTAL_SENSOR_COUNT) {
      // A slot number outside 1-9 means the record walk lost its alignment
      // (corrupt payload, or a sensor entry with an unexpected size); a
      // non-integer index means the entry ran past the end of the payload.
      break;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const flags = iresDeviceResponseReader.readUint8();
    dataOffset++;

    const dataLength = getSensorDataLengthFromSensorType(sensorType);
    const sensorBytes = [];
    for (let i = 0; i < dataLength; i++) {
      const b = iresDeviceResponseReader.readUint8();
      sensorBytes.push(b);
      dataOffset++;
    }

    // Write bytes into correct slot positions (2 bytes per sensor)
    const offset = sensorIndex * 2;
    sensorData[offset] = sensorBytes[0] ?? 0x00;
    sensorData[offset + 1] = sensorBytes[1] ?? 0x00;
  }

  return base64Buffer.encode(sensorData);
}

export function getSensorTypes(comBoxStatus: ComBoxStatus) {
  if (comBoxStatus.sensorDataExt) {
    return extractSensorTypesFromSensorDataExt(comBoxStatus.sensorDataExt);
  }

  return comBoxStatus.sensorTypes;
}

export function getSensorData(comBoxStatus: ComBoxStatus) {
  if (comBoxStatus.sensorDataExt) {
    return extractSensorDataFromSensorDataExt(comBoxStatus.sensorDataExt);
  }

  return comBoxStatus.sensorData;
}

export function getCurrentSensorData(comBoxStatus: ComBoxStatus) {
  const encodedSensorData = getSensorData(comBoxStatus);
  const sensorTypes = getSensorTypes(comBoxStatus);

  const sensorData = new Uint8Array(
    base64Buffer.decode(encodedSensorData)
  );

  return readSensorData(sensorTypes, sensorData);
}

export function getCurrentRoomSensorData(comBoxStatus: ComBoxStatus) {
  return [getCurrentSensorData(comBoxStatus)[4]];
}

export function getCurrentFloorSensorData(comBoxStatus: ComBoxStatus) {
  return getCurrentSensorData(comBoxStatus).slice(0, 4);
}

export function isLidOpen(comBox: ComBox) {
  // Sometimes, Josef needs dummy devices for testing, and for these devices, we don’t have the comBoxStatus field
  if (!comBox.comBoxStatus) {
    return false;
  }

  return comBox.comBoxStatus.comState.indexOf(ComState.LID_OPEN) >= 0;
}

export function getComBoxStateFromFlag(stateFlag: number) {
  switch (stateFlag) {
    case 11:
      return ComBoxDeviceState.TRANSITION;
    case 7:
      return ComBoxDeviceState.MALFUNCTION;
    case 5:
      return ComBoxDeviceState.DRY_RUN_ENDED;
    case 4:
      return ComBoxDeviceState.WAITING;
    case 3:
      return ComBoxDeviceState.SUSPENDED;
    case 2:
      return ComBoxDeviceState.ACTIVE;
    case 1:
      return ComBoxDeviceState.IDLE;
    case 0:
      return ComBoxDeviceState.STANDBY;
  }

  return ComBoxDeviceState.STANDBY;
}

export function getComBoxState(comBox: ComBox) {
  if (isPoweredOff(comBox)) {
    return ComBoxDeviceState.POWER_FAIL;
  }

  const stateFlag = comBox.comBoxStatus.deviceState;

  return getComBoxStateFromFlag(stateFlag);
}

function getComBoxDeviceStateDescriptionRes(comBoxState: ComBoxDeviceState) {
  switch (comBoxState) {
    case ComBoxDeviceState.POWER_FAIL:
      return "comBox.deviceState.powerFail";
    case ComBoxDeviceState.DRY_RUN_ENDED:
      return "comBox.deviceState.dryRunEnded";
    case ComBoxDeviceState.TRANSITION:
      return "comBox.deviceState.transition";
    case ComBoxDeviceState.STANDBY:
      return "comBox.deviceState.standBy";
    case ComBoxDeviceState.IDLE:
      return "comBox.deviceState.idle";
    case ComBoxDeviceState.ACTIVE:
      return "comBox.deviceState.active";
    case ComBoxDeviceState.SUSPENDED:
      return "comBox.deviceState.suspended";
    case ComBoxDeviceState.WAITING:
      return "comBox.deviceState.waiting";
    case ComBoxDeviceState.MALFUNCTION:
      return "comBox.deviceState.malfunction";
  }
}

export function getLidStateDescription(vue: Vue, comBox: ComBox) {
  const isOpen = isLidOpen(comBox);

  return vue
    .$t(isOpen ? "comBox.deviceState.lidOpen" : "comBox.deviceState.lidClosed")
    .toString();
}

export function getComBoxDeviceStateDescription(vue: Vue, comBox: ComBox) {
  const comBoxState = getComBoxState(comBox);
  return vue.$t(getComBoxDeviceStateDescriptionRes(comBoxState));
}

export enum ComBoxError {
  RTCC_MALFUNCTION = "RTCC_MALFUNCTION",
  NO_TIME_INFO = "NO_TIME_INFO",
  EEPROM_MALFUNCTION = "EEPROM_MALFUNCTION",
  SPI_FLASH_MALFUNCTION = "SPI_FLASH_MALFUNCTION",
  DISPLAY_TIMEOUT = "DISPLAY_TIMEOUT",
  ELECTRICITY_METER_TIMEOUT = "ELECTRICITY_METER_TIMEOUT",
  NO_LEAD_SENSOR = "NO_LEAD_SENSOR",
  TELAIRE_EXCEEDS_MAX_TEMPERATURE = "TELAIRE_EXCEEDS_MAX_TEMPERATURE",
  ELECTRICITY_METER_MALFUNCTION = "ELECTRICITY_METER_MALFUNCTION",

  I2C2_SENSOR_1_NO_RESPONSE = "I2C2_SENSOR_1_NO_RESPONSE",
  I2C2_SENSOR_2_NO_RESPONSE = "I2C2_SENSOR_2_NO_RESPONSE",
  I2C2_SENSOR_3_NO_RESPONSE = "I2C2_SENSOR_3_NO_RESPONSE",
  I2C2_SENSOR_4_NO_RESPONSE = "I2C2_SENSOR_4_NO_RESPONSE",
  I2C2_SENSOR_5_NO_RESPONSE = "I2C2_SENSOR_5_NO_RESPONSE",

  RELAY_DEFECTIVE = "RELAY_DEFECTIVE",

  BLE_MALFUNCTION = "BLE_MALFUNCTION",
  NB1_BOOT_FAIL = "NB1_BOOT_FAIL",
  NO_SIM = "NO_SIM",
  CONFIG_RESET = "CONFIG_RESET",
  DEVICE_CONFIG_RESET = "DEVICE_CONFIG_RESET",
  CLIENT_CONFIG_RESET = "CLIENT_CONFIG_RESET",

  UART1_OVERRUN = "UART1_OVERRUN",
  UART2_OVERRUN = "UART2_OVERRUN",
  UART3_OVERRUN = "UART3_OVERRUN",
  UART4_OVERRUN = "UART4_OVERRUN",
  UART5_OVERRUN = "UART5_OVERRUN",
}

export function getComBoxErrorFromFlag(flag: number) {
  switch (flag) {
    case 0:
      return ComBoxError.RTCC_MALFUNCTION;
    case 1:
      return ComBoxError.NO_TIME_INFO;
    case 2:
      return ComBoxError.EEPROM_MALFUNCTION;
    case 3:
      return ComBoxError.SPI_FLASH_MALFUNCTION;
    case 4:
      return ComBoxError.DISPLAY_TIMEOUT;
    case 5:
      return ComBoxError.ELECTRICITY_METER_MALFUNCTION;
    case 6:
      return ComBoxError.NO_LEAD_SENSOR;
    case 7:
      return ComBoxError.TELAIRE_EXCEEDS_MAX_TEMPERATURE;
    case 8:
      return ComBoxError.ELECTRICITY_METER_MALFUNCTION;

    case 11:
      return ComBoxError.I2C2_SENSOR_1_NO_RESPONSE;
    case 12:
      return ComBoxError.I2C2_SENSOR_2_NO_RESPONSE;
    case 13:
      return ComBoxError.I2C2_SENSOR_3_NO_RESPONSE;
    case 14:
      return ComBoxError.I2C2_SENSOR_4_NO_RESPONSE;
    case 15:
      return ComBoxError.I2C2_SENSOR_5_NO_RESPONSE;
    case 16:
      return ComBoxError.RELAY_DEFECTIVE;

    case 17:
      return ComBoxError.BLE_MALFUNCTION;
    case 18:
      return ComBoxError.NB1_BOOT_FAIL;
    case 19:
      return ComBoxError.NO_SIM;
    case 20:
      return ComBoxError.CONFIG_RESET;
    case 21:
      return ComBoxError.DEVICE_CONFIG_RESET;
    case 22:
      return ComBoxError.CLIENT_CONFIG_RESET;

    case 25:
      return ComBoxError.UART1_OVERRUN;
    case 26:
      return ComBoxError.UART2_OVERRUN;
    case 27:
      return ComBoxError.UART3_OVERRUN;
    case 28:
      return ComBoxError.UART4_OVERRUN;
    case 29:
      return ComBoxError.UART5_OVERRUN;
  }

  return null;
}

const flagsIncludedInErrorMask = new Set([0, 2, 3]);
const flagsIncludedInWarningMask = new Set([
  1,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
  13,
  14,
  15,
  17,
  18,
  19,
]);

// ==================== FIRMWARE v4.0.0+ CHANGES ====================
const flagsToBeDiscarded = [12, 13, 14];

export function filterComBoxErrorFlagsIfNeeded(flags: number[], doesSupportExtendedSensors = false) {
  return flags.filter(flag => !doesSupportExtendedSensors || !flagsToBeDiscarded.includes(flag));
}

// TODO: ==================== FIRMWARE v4.0.0+ CHANGES ====================
export function getComBoxErrorFlagsFromErrorState(errorState: Uint8Array) {
  const errors: number[] = [];

  for (let index = 0; index < 32; index++) {
    const byteIndex = 3 - Math.floor(index / 8);
    const bitIndex = index % 8;

    const mask = 1 << bitIndex;
    const targetByte = errorState[byteIndex];

    if (targetByte & mask) {
      errors.push(index);
    }
  }

  return errors;
}

interface ComBoxErrorFlagOptions {
  excludeFlagsNotIncludedInMask?: boolean;
}

export function getGroupedComBoxErrorStateFlags(comBoxStatus: ComBoxStatus, doesSupportExtendedSensors = false) {
  // Sometimes, Josef needs dummy devices for testing, and for these devices, we don’t have the comBoxStatus field
  if (!comBoxStatus) {
    return {
      errorFlags: [],
      warningFlags: [],
      infoFlags: []
    };
  }
  
  const errorState = new Uint8Array(
    base64Buffer.decode(comBoxStatus.errorState)
  );

  // ==================== FIRMWARE v4.0.0+ CHANGES ====================
  const flags = filterComBoxErrorFlagsIfNeeded(
    getComBoxErrorFlagsFromErrorState(errorState), doesSupportExtendedSensors
  );

  const errorFlags = flags.filter((flag) => flagsIncludedInErrorMask.has(flag));

  const warningFlags = flags.filter((flag) =>
    flagsIncludedInWarningMask.has(flag)
  );

  const infoFlags = flags.filter(
    (flag) =>
      flag < 30 &&
      !flagsIncludedInWarningMask.has(flag) &&
      !flagsIncludedInErrorMask.has(flag)
  );

  return {
    errorFlags,
    warningFlags,
    infoFlags,
  };
}

export function getComBoxErrorFlags(
  comBoxStatus: ComBoxStatus,
  options?: ComBoxErrorFlagOptions,
  doesSupportExtendedSensors = false
) {
  // Sometimes, Josef needs dummy devices for testing, and for these devices, we don’t have the comBoxStatus field
  if (!comBoxStatus) {
    return [];
  }

  const errorState = new Uint8Array(
    base64Buffer.decode(comBoxStatus.errorState)
  );

  // ==================== FIRMWARE v4.0.0+ CHANGES ====================
  const flags = filterComBoxErrorFlagsIfNeeded(
    getComBoxErrorFlagsFromErrorState(errorState), doesSupportExtendedSensors
  );
  const { excludeFlagsNotIncludedInMask } = options ?? {};

  if (!excludeFlagsNotIncludedInMask) {
    return flags;
  }

  return flags.filter(
    (flag) =>
      flagsIncludedInErrorMask.has(flag) || flagsIncludedInWarningMask.has(flag)
  );
}

export function getComBoxErrorFlagDescriptions(
  vue: Vue,
  errors: number[],
  args?: {
    showFlagNumber?: boolean;
    separator?: string;
    doesSupportExtendedSensors?: boolean;
  }
) {
  const { showFlagNumber, separator, doesSupportExtendedSensors } = args ?? {};

  const errorDescriptions = errors
    .map((error) => {
      // ==================== FIRMWARE v4.0.0+ CHANGES ====================
      const errorResource = (error === 11 || error === 15) && doesSupportExtendedSensors
        ? `${error}New`
        : `${error}`;
      const i18n = `comBox.errors.error${errorResource}`;
      const translated = vue.$t(i18n).toString();

      if (translated === i18n) {
        return null;
      } else {
        return `${translated}${showFlagNumber ? ` (${error})` : ""}`;
      }
    })
    .filter((value) => !!value);

  if (errorDescriptions.length > 0) {
    return errorDescriptions.join(separator ?? "\n");
  } else {
    return null;
  }
}

export function isDryRunActiveOrInterrupted(comBox: ComBox) {
  if (getComBoxState(comBox) === ComBoxDeviceState.POWER_FAIL) {

    // Sometimes, Josef needs dummy devices for testing, and for these devices, we don’t have the comBoxStatus field
    if (!comBox.comBoxStatus) {
      return false;
    }

    const lastUpdate = comBox.comBoxStatus.createdAt.toDate();
    const timeSinceLastUpdate = new Date().getTime() - lastUpdate.getTime();

    if (timeSinceLastUpdate > IRES_TIMEOUT_THRESHOLD_DEFAULT_IN_MS) {
      return false;
    }
  }

  if (comBox.comBoxStatus.dryRunActive) {
    return true;
  }

  if (getComBoxState(comBox) === ComBoxDeviceState.WAITING) {
    return true;
  }

  return false;
}

export function isWaitingForNewProgramChain(
  comBox: ComBox,
  programChain: ProgramChain
): boolean {
  if (!comBox || !programChain || !comBox.comBoxStatus
  ) {
    return false;
  }

  if (!comBox.comBoxStatus.dryRunActive) {
    return false;
  }

  return !(
    programChain.runs[programChain.runs.length - 1]?.programRunID ===
      comBox.comBoxStatus.latestRunId ||
    programChain.runs[0]?.programRunID == comBox.comBoxStatus.dryRunFirstRunId
  );
}

export function getComBoxDryingPrograms(
  comBox: ComBox,
  vue: Vue
): Array<ComBoxDryingProgram> {
  if (comBox.dryingConfigInfo) {
    return getComBoxDryingProgramsDryingConfigInfo(comBox.dryingConfigInfo, comBox.serialNo);
  } else {
    return getDefaultDryingPrograms(vue);
  }
}

export function isMonitorProgramOfComBox(programNumber: number) {
  return programNumber === 15 || programNumber === 3;
}

export function isPostDryingProgram(programNumber: number) {
  return programNumber === 7;
}

export function isRoomDryingProgramRevision0(programNumber: number) {
  return programNumber === 4;
}

export function isRoomDryingProgramRevision1(programNumber: number) {
  return programNumber === 8;
}

export function isRoomDryingProgram(programNumber: number) {
  return isRoomDryingProgramRevision0(programNumber) || isRoomDryingProgramRevision1(programNumber);
}

export function isTargetDryingProgram(programNumber: number) {
  return programNumber === 5;
}

export function isProgramNumberUndefined(programNumber: number) {
  return programNumber === 255;
}

export function isProgram0OfPanel(programNumber: number) {
  return programNumber === 0;
}

export function isProgram1To7OfPanel(programNumber: number) {
  return programNumber > 0 && programNumber <= 7;
}

export function getComBoxProgramName(
  vue: Vue,
  programNumber: number,
  comBox?: ComBox
) {
  if (isRoomDryingProgram(programNumber)) {
    return vue
      .$t("comBoxDetail.controlConfig.dryingModeOptions.roomDrying")
      .toString();
  } else if (isMonitorProgramOfComBox(programNumber)) {
    return vue
      .$t("comBoxDetail.controlConfig.dryingModeOptions.monitor")
      .toString();
  } else if (programNumber === 0) {
    return vue
      .$t("comBoxDetail.controlConfig.dryingModeOptions.negativePressure")
      .toString();
  } else if (programNumber === 1) {
    return vue
      .$t("comBoxDetail.controlConfig.dryingModeOptions.pushPull")
      .toString();
  } else if (programNumber === 2) {
    return vue
      .$t("comBoxDetail.controlConfig.dryingModeOptions.positivePressure")
      .toString();
  } else if (isTargetDryingProgram(programNumber)) {
    return vue
      .$t("comBoxDetail.controlConfig.dryingModeOptions.tentDrying")
      .toString();
  } else if (programNumber === 7) {
    return vue.$t("comBoxDetail.postDryingRun").toString();
  }

  if (!comBox) {
    return vue.$t("panelDetail.runInfo.programPX", [programNumber]).toString();
  }

  const programs = getComBoxDryingPrograms(comBox, vue);

  const matchingProgram = programs.find(
    (program) => program.programNumber === programNumber
  );

  if (matchingProgram) {
    return matchingProgram.name;
  }

  return vue.$t("panelDetail.runInfo.programPX", [programNumber]).toString();
}

export function getMcuResetSourcesString(vue: Vue, comBox: ComBox) {
  const source = comBox.mcuResetSource;

  if (source === undefined) {
    return "";
  }

  const result: string[] = [];

  if (source & 1) {
    result.push(
      vue.$t("comBoxSettings.mcuResetSourceValues.resetPin").toString()
    );
  }

  if (source & 2) {
    result.push(
      vue.$t("comBoxSettings.mcuResetSourceValues.powerOn").toString()
    );
  }

  if (source & 4) {
    result.push(
      vue.$t("comBoxSettings.mcuResetSourceValues.software").toString()
    );
  }

  if (source & 8) {
    result.push(
      vue
        .$t("comBoxSettings.mcuResetSourceValues.independentWatchdog")
        .toString()
    );
  }

  if (source & 16) {
    result.push(
      vue.$t("comBoxSettings.mcuResetSourceValues.windowWatchdog").toString()
    );
  }

  if (source & 32) {
    result.push(
      vue.$t("comBoxSettings.mcuResetSourceValues.lowPower").toString()
    );
  }

  return result.join(", ");
}